/** Bewijs uit de serveruitvoering, nooit uit modelparameters of een klantmail. */
export interface OpdrachtBewijs {
  id: string;
  nummer: string;
  klantId: string;
  bronWerkOrderId: string;
  pakketSku: string;
  totaalInclBtw: number;
  regels: Array<{ sku: string; lengteCm: number | null; breedteCm: number | null; diameterCm: number | null; oppervlakteM2: number }>;
  /** Exact own-order link, verified by the server; absent on older outcomes. */
  planningUrl?: string;
}

export interface BevestigingBewijs {
  /** Verstuurd = provider heeft geaccepteerd, niet bewezen afgeleverd. */
  status: 'verstuurd' | 'niet_verstuurd' | 'onzeker' | 'wacht_op_verzendklik';
  providerId?: string;
  reden?: string;
}

const recordId = (v: unknown): v is string => typeof v === 'string'
  && /^(?:[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?|eigen-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/.test(v);
const positief = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const PAKKETTEN = new Set(['WAS-PACK-VLOERKLEED-BASIS', 'WAS-PACK-VLOERKLEED-PROTECT', 'WAS-PACK-VLOERKLEED-PET', 'WAS-PACK-VLOERKLEED-PREMIUM']);

export function leesOpdrachtBewijs(raw: unknown): OpdrachtBewijs | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as OpdrachtBewijs;
  if (!recordId(o.id) || !recordId(o.klantId) || !recordId(o.bronWerkOrderId) || o.id === o.bronWerkOrderId
    || typeof o.nummer !== 'string' || !/^WB-\d+$/.test(o.nummer) || !PAKKETTEN.has(o.pakketSku)
    || !positief(o.totaalInclBtw) || !Array.isArray(o.regels) || !o.regels.length || o.regels.length > 20
    || o.regels.some(r => !r || r.sku !== 'WAS-PRIM-VLOERKLEED-REINIGEN' || !positief(r.oppervlakteM2)
      || (r.diameterCm !== null ? !positief(r.diameterCm) || r.lengteCm !== null || r.breedteCm !== null
        : !positief(r.lengteCm) || !positief(r.breedteCm)))) return undefined;
  return { id: o.id, nummer: o.nummer, klantId: o.klantId, bronWerkOrderId: o.bronWerkOrderId,
    pakketSku: o.pakketSku, totaalInclBtw: o.totaalInclBtw,
    ...(geldigeOpdrachtPlanningUrl(o.planningUrl) ? { planningUrl: o.planningUrl } : {}),
    regels: o.regels.map(r => ({ sku: r.sku, lengteCm: r.lengteCm, breedteCm: r.breedteCm,
      diameterCm: r.diameterCm, oppervlakteM2: r.oppervlakteM2 })) };
}

export function leesBevestigingBewijs(raw: unknown): BevestigingBewijs | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const b = raw as BevestigingBewijs;
  if (!['verstuurd', 'niet_verstuurd', 'onzeker', 'wacht_op_verzendklik'].includes(b.status)) return undefined;
  const providerId = typeof b.providerId === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(b.providerId) ? b.providerId : undefined;
  if (b.status === 'verstuurd' && !providerId) return undefined;
  return { status: b.status, ...(b.status !== 'wacht_op_verzendklik' && providerId ? { providerId } : {}),
    ...(typeof b.reden === 'string' ? { reden: b.reden.slice(0, 500) } : {}) };
}

export function geldigeOpdrachtPlanningUrl(url: unknown): url is string {
  return typeof url === 'string' && /^https:\/\/client\.kleda\.nl\?token=[a-f0-9]{36}&type=pickup$/.test(url);
}

/** The reviewed answer carries the new link; no second confirmation is sent. */
export function metOpdrachtLinks(draft: string, acties: Array<{ verb: string; uitkomst?: { besluit: string; ok?: boolean; opdracht?: OpdrachtBewijs } }>): string {
  const links = new Map<string, OpdrachtBewijs>();
  for (const a of acties) {
    const o = a.uitkomst?.besluit === 'akkoord' && a.uitkomst.ok === true && a.verb === 'maak_vervolgopdracht'
      ? leesOpdrachtBewijs(a.uitkomst.opdracht) : undefined;
    if (o?.planningUrl && !draft.includes(o.planningUrl)) links.set(o.id, o);
  }
  return [draft, ...[...links.values()].map(o => `Kies je ophaaldag voor ${o.nummer}: ${o.planningUrl}`)].filter(Boolean).join('\n\n');
}

/** Eén regel voor alle gates (harnas, actie-endpoint): de opdracht is bewezen als de
 * S01 is aangeboden (verstuurd + providerId) óf als de service hem bewust vasthoudt
 * voor de aparte verzendklik en de persoonlijke link al bewezen is. Sinds 09-09 is
 * dat laatste het normale pad; 'verstuurd' als enige maat kleurde elke geslaagde
 * vervolgopdracht rood (Bruns WB-324263, 17-09). */
export function vervolgopdrachtBewezen(bewijs: { opdracht?: OpdrachtBewijs; bevestiging?: BevestigingBewijs }): boolean {
  if (!bewijs.opdracht || !bewijs.bevestiging) return false;
  if (bewijs.bevestiging.status === 'verstuurd') return true;
  return bewijs.bevestiging.status === 'wacht_op_verzendklik' && geldigeOpdrachtPlanningUrl(bewijs.opdracht.planningUrl);
}

/** Ook bij een mailfout blijft de aantoonbaar aangemaakte opdracht zichtbaar. */
export function vervolgopdrachtResultaat(verb: string, body: Record<string, unknown>): { opdracht?: OpdrachtBewijs; bevestiging?: BevestigingBewijs } {
  if (verb !== 'maak_vervolgopdracht') return {};
  const opdracht = leesOpdrachtBewijs(body.opdracht);
  if (!opdracht) return {};
  return { opdracht, bevestiging: leesBevestigingBewijs(body.bevestiging) };
}
